import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Sticky from 'react-stickynode';
import styled, { useTheme } from 'styled-components';
import { border } from 'styled-system';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import PromoCodeValidation from 'hb-react/shared/apps/Events/components/EventRegistrationContainer/components/PromoCode/PromoCodeValidation';
import { useEventCartContext } from 'hb-react/shared/apps/Events/contexts/EventCartContext';
import { useTransformShoppingCard } from 'hb-react/shared/apps/Events/hooks/useTransformShoppingCard';
import { transformPromoCodes } from 'hb-react/shared/apps/Events/utils/cartUtils/cartItemsUtils';
import ShoppingCard from 'hb-react/shared/components/ShoppingCard';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

import ShoppingCardLine from './ShoppingCardLine';
import RegistrationEventCard from '../RegistrationEventCard';

const EventRegistrationSidebar = ({ event, withShoppingCard = false }) => {
  const breakpoint = useBreakpoint();
  const isMobile = useMemo(() => ['s', 'm'].includes(breakpoint), [breakpoint]);
  const { flagDraftCartRefactor, flagEvPaymentsMovePromocode } =
    useFeatureFlag();

  const { pathname } = useLocation();

  const { data } = useEventCartContext() || {};

  const eligibleForPromocodes =
    data?._metadata?.eligible_for_promocodes ?? false;

  const withPromoCodeSection =
    eligibleForPromocodes &&
    flagDraftCartRefactor &&
    flagEvPaymentsMovePromocode &&
    !pathname.includes('success');

  return (
    <Sticky
      enabled={!isMobile}
      top="header"
      bottomBoundary="#event-registration-container"
    >
      {({ status }) => (
        <Box flexDirection="column">
          <AsideWrapper
            p={0}
            mb={4}
            $isSticked={!withPromoCodeSection && status === Sticky.STATUS_FIXED}
          >
            <ShadowedWrapper $withShoppingCard={withShoppingCard}>
              <RegistrationEventCard event={event} />
            </ShadowedWrapper>
            {withShoppingCard && (
              <Box display="block" data-testid="event-shopping-card">
                <EventShoppingCard isMobile={isMobile} />
              </Box>
            )}
          </AsideWrapper>

          {withPromoCodeSection && (
            <Card p={4} mt={4}>
              <PromoCodeValidation />
            </Card>
          )}
        </Box>
      )}
    </Sticky>
  );
};

const EventShoppingCard = ({ isMobile }) => {
  const {
    tickets = [],
    addons = [],
    donations = [],
  } = useTransformShoppingCard();
  const { data } = useEventCartContext() || {};
  const { flagEvCurrenciesWithoutCents } = useFeatureFlag();
  const theme = useTheme();

  if (data?.cart?.cart_items_by_quantity) {
    return (
      <ShoppingCard
        items={data.cart.cart_items_by_quantity}
        displayInModal={isMobile}
      />
    );
  }

  const promoCodes = transformPromoCodes(
    data?.cart?.cart_items || [],
    data?.cart?.currency?.code,
    flagEvCurrenciesWithoutCents,
  );

  // Sub addons are kept in tickets for display, and spread to items for total amount calculation
  const subAddons = tickets.flatMap((ticket) => ticket.addons);
  const items = [
    ...tickets,
    ...addons,
    ...donations,
    ...subAddons,
    ...promoCodes,
  ].map((item) => ({
    ...item,
    render: () => <ShoppingCardLine item={item} />,
  }));

  return (
    <ShoppingCard
      items={items}
      displayInModal={isMobile}
      wrapperProps={{
        borderRadius: {
          _: 0,
          l: `0 0 ${theme.themeVariables.globalBorderRadius}px ${theme.themeVariables.globalBorderRadius}px`,
        },
      }}
    />
  );
};

EventRegistrationSidebar.propTypes = {
  withShoppingCard: PropTypes.bool,
  event: PropTypes.object.isRequired,
};

export default EventRegistrationSidebar;

const ShadowedWrapper = styled(Box).attrs(({ theme, $withShoppingCard }) => ({
  display: 'block',
  borderTopLeftRadius: theme.themeVariables.globalBorderRadius,
  borderTopRightRadius: theme.themeVariables.globalBorderRadius,
  borderBottomLeftRadius: $withShoppingCard
    ? { _: theme.themeVariables.globalBorderRadius, l: 0 }
    : theme.themeVariables.globalBorderRadius,
  borderBottomRightRadius: $withShoppingCard
    ? { _: theme.themeVariables.globalBorderRadius, l: 0 }
    : theme.themeVariables.globalBorderRadius,
}))`
  ${border}
  width: 100%;
`;

const AsideWrapper = styled(Card)`
  transform: translateY(${(props) => (props.$isSticked ? -25 : 0)}px);
  transition: transform 0.2s;
`;
